import React, { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNewBreadcrumb } from "recoils";
import { DashboardTabs, MarginWrapper } from "components";
import {
  useEmissionFactors,
  useEmissionFactorsBySite,
  useScientificUnit,
} from "hooks";
import {
  getEmissionFactorsBySiteId,
  getEmissionFactorsExcel,
  getCurrencyExcel,
  getScientificUnitExcel,
  getEmissionFactorsBySiteExcel,
} from "api";
import { EMISSION_FACTORS_PAGE } from "./consts";
import {
  CURRENCY_COLUMNS,
  EMISSION_FACTORS_BY_SITE_COLUMNS,
  EMISSION_FACTORS_COLUMNS,
  ReferencesTable,
  SCIENTIFIC_UNIT_COLUMNS,
} from "./components";

const EXPORT_FUNCTIONS = [
  getEmissionFactorsExcel,
  getCurrencyExcel,
  getScientificUnitExcel,
  getEmissionFactorsBySiteExcel,
];

export const EmissionFactorsPage = () => {
  useNewBreadcrumb(EMISSION_FACTORS_PAGE.TITLE);

  // ---- Hooks ----
  // eslint-disable-next-line no-restricted-globals
  const queryParams = new URLSearchParams(location.search);
  const siteId = queryParams.get("s");

  // ---- States ----
  const [activeTab, setActiveTab] = React.useState(0);
  const { data: references, isLoading: isLoadingEmissionFactors } = useEmissionFactors(activeTab === 0);
  const { data: emissionFactorsData, isLoading: isLoadingBySite } = useEmissionFactorsBySite(activeTab === 3);
  const { data: emissionFactorsDataById, mutateAsync: emissionFactorMutation } =
    useMutation(getEmissionFactorsBySiteId);
  const { data: scientificunitandcurrency, isLoading: isLoadingScientificUnit } = useScientificUnit(activeTab === 1 || activeTab === 2);
  const emissionsFactorData = references || [];
  const scientificUnit = scientificunitandcurrency?.scientificUnit;
  const currencyData = scientificunitandcurrency?.currency;

  const getParsedData = (data: string[]) =>
    data?.map((str: string, index: any) => {
      try {
        return JSON.parse(str);
      } catch (error) {
        console.error(`Error parsing string at index ${index}:`, error);
        return null;
      }
    });

  const leagalBtnStates = {
    LEGAL: "References",
  };

  const { LEGAL } = leagalBtnStates;

  const tabs = [
    {
      label: "Emissions Factors",
      content: (
        <ReferencesTable
          columns={EMISSION_FACTORS_COLUMNS}
          referencesData={emissionsFactorData}
          isLoading={isLoadingEmissionFactors}
        />
      ),
    },
    {
      label: "Currency",
      content: (
        <ReferencesTable
          columns={CURRENCY_COLUMNS}
          referencesData={getParsedData(currencyData)}
          isLoading={isLoadingScientificUnit}
        />
      ),
    },
    {
      label: "Scientific Unit",
      content: (
        <ReferencesTable
          columns={SCIENTIFIC_UNIT_COLUMNS}
          referencesData={getParsedData(scientificUnit)}
          isLoading={isLoadingScientificUnit}
        />
      ),
    },
    {
      label: "Emission Factors by Site",
      content: (
        <ReferencesTable
          columns={EMISSION_FACTORS_BY_SITE_COLUMNS}
          referencesData={
            siteId ? emissionFactorsDataById : emissionFactorsData
          }
          isLoading={isLoadingBySite}
        />
      ),
    },
  ];

  const handleTabChange = (tabIndex: number) => {
    setActiveTab(tabIndex);
  };

  useEffect(() => {
    if (siteId) {
      emissionFactorMutation(siteId);
    }
  }, [siteId, emissionFactorMutation]);

  return (
    <MarginWrapper>
      <DashboardTabs
        intensity
        refrences
        tabs={tabs}
        selectedBtnOption={LEGAL}
        onTabChange={handleTabChange}
        showExportButton
        exportFunction={EXPORT_FUNCTIONS[activeTab]}
      />
    </MarginWrapper>
  );
};
